import type { CSSProperties, FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

export type ChapterPoint = {
    before?: string
    strong: string
    after?: string
}

export type Chapter = {
    label: string
    title: string
    text: string
    points?: ChapterPoint[]
    image: string
    imageAlt: string
    tag?: string
}

interface FeatureChaptersProps {
    chapters: Chapter[]
    index?: string
    eyebrow?: string
    title: string
    intro?: string
    tone?: Tone
    mediaSide?: 'left' | 'right'
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

const Point: FC<{ point: ChapterPoint }> = ({ point }): ReactNode => (
    <>
        {point.before && <>{point.before} </>}
        <strong className='font-semibold text-tone-fg'>{point.strong}</strong>
        {point.after && <> {point.after}</>}
    </>
)

/**
 * Feature chapters: the text scrolls while a framed screen stays pinned and
 * wipes to the image of the chapter crossing the middle of the viewport.
 */
export const FeatureChapters: FC<FeatureChaptersProps> = ({
    chapters,
    index,
    eyebrow = 'Slik fungerer det',
    title,
    intro,
    tone = 'paper',
    mediaSide = 'right',
    id = 'slik-fungerer-det'
}): ReactNode => {
    const [active, setActive] = useState(0)
    const refs = useRef<Array<HTMLDivElement | null>>([])

    useEffect(() => {
        const nodes = refs.current.filter((node): node is HTMLDivElement => node !== null)

        if (!nodes.length || !('IntersectionObserver' in window)) return

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return

                const i = Number((entry.target as HTMLElement).dataset.chapter)

                if (!Number.isNaN(i)) setActive(i)
            })
        }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 })

        nodes.forEach(node => observer.observe(node))

        return () => observer.disconnect()
    }, [chapters.length])

    const media = (
        <div className='col-span-12 hidden lg:col-span-6 lg:block'>
            <div className='sticky top-[12vh] h-[76vh]'>
                <div className='relative h-full overflow-hidden bg-tone-line'>
                    {chapters.map((chapter, i) => (
                        <div
                            key={chapter.label}
                            className='chapter-media absolute inset-0'
                            data-state={i === active ? 'active' : i < active ? 'past' : 'next'}
                        >
                            <img
                                src={chapter.image}
                                alt={chapter.imageAlt}
                                loading='lazy'
                                decoding='async'
                                className='size-full object-cover'
                            />
                        </div>
                    ))}

                    <div className='pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between p-5 text-paper'>
                        <span className='t-label bg-night px-2 py-1'>{chapters[active].label}</span>
                        <span className='t-label tabular bg-night px-2 py-1'>
                            {String(active + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <section
            id={id}
            className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative py-24 md:py-36')}
            data-header={DARK.includes(tone) ? 'dark' : 'light'}
        >
            <div className='wrap'>
                <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                    <div className='col-span-12 lg:col-span-7'>
                        <SectionLabel index={index}>{eyebrow}</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            <SplitWords text={title} />
                        </h2>
                    </div>

                    {intro && (
                        <p className='col-span-12 self-end text-tone-muted t-lead pretty lg:col-span-4 lg:col-start-9'>
                            {intro}
                        </p>
                    )}
                </div>

                <div className='mt-16 grid grid-cols-12 gap-x-6 md:mt-24'>
                    {mediaSide === 'left' && media}

                    <div className={cn('col-span-12 lg:col-span-5', mediaSide === 'right' ? 'lg:col-start-1' : 'lg:col-start-8')}>
                        {chapters.map((chapter, i) => (
                            <div
                                key={chapter.label}
                                ref={node => {
                                    refs.current[i] = node
                                }}
                                data-chapter={i}
                                className='flex flex-col justify-center border-t border-tone-line py-12 lg:min-h-[76vh] lg:py-16'
                            >
                                <div className='mb-8 aspect-[4/3] overflow-hidden bg-tone-line lg:hidden' data-reveal='clip'>
                                    <img src={chapter.image} alt={chapter.imageAlt} loading='lazy' decoding='async' className='size-full object-cover' />
                                </div>

                                <p className='t-label flex items-center gap-3 text-tone-muted'>
                                    <span className='tabular text-tone-ink'>{String(i + 1).padStart(2, '0')}</span>
                                    <span>{chapter.label}</span>
                                    {chapter.tag && <span className='border border-tone-line px-2 py-0.5'>{chapter.tag}</span>}
                                </p>

                                <h3 className='t-h2 mt-5 balance'>{chapter.title}</h3>
                                <p className='mt-5 max-w-lg text-tone-muted t-body pretty'>{chapter.text}</p>

                                {chapter.points && (
                                    <ul className='mt-8 flex flex-col gap-3'>
                                        {chapter.points.map((point, p) => (
                                            <li
                                                key={point.strong}
                                                className='flex gap-4 text-tone-muted'
                                                data-reveal='rise'
                                                style={{ '--d': p } as CSSProperties}
                                            >
                                                <span className='mt-[0.6em] h-px w-5 shrink-0 bg-tone-accent' aria-hidden='true' />
                                                <span><Point point={point} /></span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>

                    {mediaSide === 'right' && <div className='hidden lg:col-span-1 lg:block' aria-hidden='true' />}
                    {mediaSide === 'right' && media}
                </div>
            </div>
        </section>
    )
}
